import { reaction, runInAction } from 'mobx'
import { toApiError } from '@/api/errors'
import { debounce } from '@/lib/debounce'
import { keysWithPrefix, onStorageWrite, readJSON, removeKey, writeJSON } from '@/lib/storage'
import { settingsService, type AppearanceSettings } from '@/services'
import { DEFAULT_APPEARANCE } from './AppearanceStore'
import type { RootStore } from './RootStore'

/**
 * Keeps the admin's per-user UI preferences in the account (GET/PUT/PATCH/DELETE
 * /v1/me/settings/:key), so they follow the user to another browser.
 *
 * localStorage stays the first-paint cache and what every page reads: the
 * boot script and the stores apply the last known look at once, and this
 * store only moves values between that cache and four server documents:
 *
 *   admin.appearance  look + theme mode, table density, collapsed sidebar
 *                     (everything «Сбросить по умолчанию» resets)
 *   admin.tables      hidden columns per table: { [tableId]: { hidden } }
 *   admin.reports     procurement report grouping and remembered filters,
 *                     the planner's month/week view, the finance reports'
 *                     remembered filters (balance, P&L, cash flow)
 *   admin.pages       the orders page scope, open sidebar folders
 *
 * Sign-in loads the documents and applies them over the defaults. A change is
 * applied at once and saved ~800 ms later: PATCH (merge patch of what changed)
 * when the stored value is known, else PUT; a document back at its defaults is
 * deleted. A 409 reloads that document and re-applies the user's change on top.
 * A failed save stays marked and is retried on the next change or sign-in;
 * nothing here ever blocks the UI.
 *
 * Accounts never share a browser's preferences: sign-out resets the cache to
 * the defaults, and changes that could not be saved wait under the profile id
 * for that profile's next sign-in. Language, working branch and the tokens
 * are not settings documents.
 */

type Json = null | boolean | number | string | Json[] | { [key: string]: Json }
type JsonObject = { [key: string]: Json }

export const SETTING_KEYS = ['admin.appearance', 'admin.tables', 'admin.reports', 'admin.pages'] as const
type SettingKey = (typeof SETTING_KEYS)[number]

type SyncState = { owner: string; dirty: SettingKey[] }

const STATE_KEY = 'mf.settings.state'
const PENDING_PREFIX = 'mf.settings.pending.'
const SAVE_DELAY_MS = 800
const SIGN_OUT_WAIT_MS = 2000

// The pages' own localStorage keys (ResourcePage, ReportsPage, PlannerView,
// FinanceReportPage, OrdersPage, Sidebar); they stay the pages' contract.
const COLUMNS_PREFIX = 'mf.cols.'
const REPORT_GROUPING_KEY = 'mf.procurement.reports.grouping'
const REPORT_FILTERS_KEY = 'mf.procurement.reports.filters'
const PLANNER_VIEW_KEY = 'mf.procurement.planner.view'
const FINANCE_REPORT_FILTERS_KEY = 'mf.finance.reports.filters'
const ORDERS_SCOPE_PREFIX = 'mf.orders.scope.'
const SIDEBAR_OPEN_PREFIX = 'mf.ui.sidebar.open.'

const isKey = (value: unknown): value is SettingKey => SETTING_KEYS.includes(value as SettingKey)
const isObject = (value: unknown): value is JsonObject => typeof value === 'object' && value !== null && !Array.isArray(value)

function documentOf(storageKey: string): SettingKey | null {
  if (storageKey.startsWith(COLUMNS_PREFIX)) return 'admin.tables'
  if (storageKey === REPORT_GROUPING_KEY || storageKey === REPORT_FILTERS_KEY || storageKey === PLANNER_VIEW_KEY || storageKey === FINANCE_REPORT_FILTERS_KEY)
    return 'admin.reports'
  if (storageKey.startsWith(ORDERS_SCOPE_PREFIX) || storageKey.startsWith(SIDEBAR_OPEN_PREFIX)) return 'admin.pages'
  return null
}

function sameJson(a: Json | undefined, b: Json | undefined): boolean {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((item, index) => sameJson(item, b[index]))
  if (isObject(a) && isObject(b)) {
    const keys = Object.keys(a).filter((key) => a[key] !== undefined)
    return keys.length === Object.keys(b).filter((key) => b[key] !== undefined).length && keys.every((key) => sameJson(a[key], b[key]))
  }
  return false
}

/** An object member holding null cannot travel in a merge patch (null means "remove"). */
function holdsNull(value: Json): boolean {
  return isObject(value) && Object.values(value).some((member) => member === null || holdsNull(member))
}

/** The RFC 7396 merge patch turning `from` into `to`; undefined when a null value makes that impossible. */
function mergePatchBetween(from: JsonObject, to: JsonObject): JsonObject | undefined {
  const patch: JsonObject = {}
  for (const key of Object.keys(from)) if (to[key] === undefined) patch[key] = null
  for (const [key, value] of Object.entries(to)) {
    const previous = from[key]
    if (value === undefined || sameJson(previous, value)) continue
    if (value === null) return undefined
    if (isObject(value) && isObject(previous)) {
      const nested = mergePatchBetween(previous, value)
      if (!nested) return undefined
      patch[key] = nested
      continue
    }
    if (holdsNull(value)) return undefined
    patch[key] = value
  }
  return patch
}

function applyMergePatch(target: Json | undefined, patch: Json): Json {
  if (!isObject(patch)) return patch
  const result: JsonObject = isObject(target) ? { ...target } : {}
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) delete result[key]
    else result[key] = applyMergePatch(result[key], value)
  }
  return result
}

export class SettingsSyncStore {
  private readonly root: RootStore
  /** The signed-in profile whose documents the cache holds; null when signed out. */
  private owner: string | null = null
  /** No state was stored yet: this browser's remembered choices predate syncing and are uploaded once. */
  private adoptLocal = false
  private loaded = false
  private loading: Promise<boolean> | null = null
  private versions: Partial<Record<SettingKey, number>> = {}
  /** What the server holds per document, the base of the next merge patch. */
  private synced: Partial<Record<SettingKey, JsonObject>> = {}
  private dirty = new Set<SettingKey>()
  private applying = false
  private flushing: Promise<void> | null = null
  private flushAgain = false
  private abort: AbortController | null = null
  private readonly scheduleFlush = debounce(() => void this.flush(), SAVE_DELAY_MS)

  constructor(root: RootStore) {
    this.root = root
    reaction(
      () => JSON.stringify(this.read('admin.appearance', null)),
      () => this.changed('admin.appearance'),
    )
    onStorageWrite((storageKey) => {
      const key = documentOf(storageKey)
      if (key) this.changed(key)
    })
  }

  /** After sign-in or a restored session: load the account's documents over this browser's cache. */
  async start(profileId: string) {
    const state = readJSON<SyncState | null>(STATE_KEY, null)
    if (state?.owner && state.owner !== profileId) this.stashAndReset(state.owner, state.dirty ?? [])

    this.owner = profileId
    this.adoptLocal = !state?.owner
    this.loaded = false
    this.loading = null
    this.versions = {}
    this.synced = {}
    this.dirty = new Set(state?.owner === profileId ? (state.dirty ?? []).filter(isKey) : [])
    this.abort = new AbortController()

    // Changes this profile made last time that never reached the server.
    const pending = readJSON<Partial<Record<SettingKey, JsonObject>> | null>(PENDING_PREFIX + profileId, null)
    if (pending) {
      for (const key of SETTING_KEYS) {
        if (!isObject(pending[key])) continue
        this.apply(key, pending[key], profileId)
        this.dirty.add(key)
      }
      removeKey(PENDING_PREFIX + profileId)
    }
    this.saveState()

    if (await this.load()) await this.flush()
  }

  /** Before an explicit sign-out: give unsaved changes a short chance to reach the server. */
  async settle() {
    this.scheduleFlush.cancel()
    if (!this.owner || this.dirty.size === 0) return
    await Promise.race([this.flush(), new Promise((resolve) => setTimeout(resolve, SIGN_OUT_WAIT_MS))])
  }

  /** Sign-out or an ended session: nothing of this account stays applied in the browser. */
  stop() {
    this.scheduleFlush.cancel()
    this.abort?.abort()
    const state = readJSON<SyncState | null>(STATE_KEY, null)
    const owner = this.owner ?? state?.owner
    if (owner) this.stashAndReset(owner, this.owner ? [...this.dirty] : (state?.dirty ?? []))
    this.owner = null
    this.loaded = false
    this.loading = null
    this.versions = {}
    this.synced = {}
    this.dirty.clear()
    this.abort = null
    removeKey(STATE_KEY)
  }

  private changed(key: SettingKey) {
    if (this.applying || !this.owner) return
    this.dirty.add(key)
    this.saveState()
    this.scheduleFlush()
  }

  /** Loads every document once per sign-in; false while the server cannot be reached. */
  private load(): Promise<boolean> {
    if (this.loaded) return Promise.resolve(true)
    if (this.loading) return this.loading

    const owner = this.owner
    this.loading = settingsService
      .list(SETTING_KEYS, { signal: this.abort?.signal })
      .then((items) => {
        if (owner !== this.owner) return false
        for (const key of SETTING_KEYS) {
          const remote = items[key]
          const value = isObject(remote?.value) ? remote.value : undefined
          this.versions[key] = value ? remote.version : 0
          this.synced[key] = value
          // The user's unsaved change wins; it is saved against the fresh version.
          if (this.dirty.has(key)) continue
          if (value) this.apply(key, value)
          else if (this.adoptLocal && !sameJson(this.read(key), this.defaults(key))) this.dirty.add(key)
          else this.apply(key, undefined)
        }
        this.loaded = true
        this.saveState()
        return true
      })
      .catch(() => false)
      .finally(() => {
        this.loading = null
      })
    return this.loading
  }

  private flush(): Promise<void> {
    if (this.flushing) {
      this.flushAgain = true
      return this.flushing
    }
    this.flushing = this.saveDirty().finally(() => {
      this.flushing = null
      if (this.flushAgain) {
        this.flushAgain = false
        void this.flush()
      }
    })
    return this.flushing
  }

  private async saveDirty() {
    const owner = this.owner
    if (!owner || this.dirty.size === 0) return
    if (!(await this.load())) return
    for (const key of SETTING_KEYS) {
      if (owner !== this.owner) return
      if (this.dirty.has(key)) await this.save(key)
    }
  }

  private async save(key: SettingKey) {
    const owner = this.owner
    const signal = this.abort?.signal
    const value = this.read(key)
    const base = this.synced[key]
    const version = this.versions[key] ?? 0
    this.dirty.delete(key)
    try {
      if (sameJson(value, this.defaults(key))) {
        if (base || version > 0) await settingsService.remove(key, { signal })
        if (owner !== this.owner) return
        this.versions[key] = 0
        this.synced[key] = undefined
        return
      }
      if (base && sameJson(base, value)) return
      const patch = base ? mergePatchBetween(base, value) : undefined
      const stored = patch
        ? await settingsService.patch(key, patch, version, { signal })
        : await settingsService.put(key, value, version, { signal })
      if (owner !== this.owner) return
      this.versions[key] = stored.version
      this.synced[key] = value
    } catch (error) {
      if (owner !== this.owner) return
      const apiError = toApiError(error)
      if (apiError.isConflict) await this.reconcile(key)
      // A value the server refuses (too large, malformed) would fail again: drop it.
      else if (apiError.status !== 400 && apiError.status !== 413) this.dirty.add(key)
    } finally {
      if (owner === this.owner) this.saveState()
    }
  }

  /** Another tab or device saved first: take its document, put the user's own change back on top, save again. */
  private async reconcile(key: SettingKey) {
    const owner = this.owner
    const latest = this.read(key)
    const base = this.synced[key]
    const change = base ? mergePatchBetween(base, latest) : undefined
    try {
      const remote = await settingsService.get(key, { signal: this.abort?.signal })
      if (owner !== this.owner) return
      const value = isObject(remote.value) ? remote.value : undefined
      this.versions[key] = value ? remote.version : 0
      this.synced[key] = value
      const merged = change && value ? applyMergePatch(value, change) : latest
      if (isObject(merged)) this.apply(key, merged)
    } catch {
      /* offline: saved again with the next change or sign-in */
    }
    this.dirty.add(key)
    this.flushAgain = true
  }

  /** Keeps the owner's unsaved documents for their next sign-in, then restores the defaults. */
  private stashAndReset(owner: string, dirty: readonly string[]) {
    const pending: Partial<Record<SettingKey, JsonObject>> = {}
    for (const key of dirty.filter(isKey)) pending[key] = this.read(key, owner)
    if (Object.keys(pending).length > 0) writeJSON(PENDING_PREFIX + owner, pending)
    for (const key of SETTING_KEYS) this.apply(key, undefined, owner)
  }

  private saveState() {
    if (this.owner) writeJSON(STATE_KEY, { owner: this.owner, dirty: [...this.dirty] } satisfies SyncState)
  }

  private defaults(key: SettingKey): JsonObject {
    if (key === 'admin.appearance') return { ...DEFAULT_APPEARANCE, theme: 'light', density: 'compact', sidebarCollapsed: false }
    return {}
  }

  /** The document as the browser holds it now. */
  private read(key: SettingKey, owner: string | null = this.owner): JsonObject {
    const { ui, appearance } = this.root
    const doc: JsonObject = {}
    switch (key) {
      case 'admin.appearance':
        return { ...appearance.settings, theme: ui.theme, density: ui.density, sidebarCollapsed: ui.sidebarCollapsed }
      case 'admin.tables':
        for (const storageKey of keysWithPrefix(COLUMNS_PREFIX)) {
          const hidden = readJSON<unknown>(storageKey, [])
          if (Array.isArray(hidden) && hidden.length > 0) doc[storageKey.slice(COLUMNS_PREFIX.length)] = { hidden: hidden.map(String) }
        }
        return doc
      case 'admin.reports': {
        const grouping = readJSON<Json>(REPORT_GROUPING_KEY, null)
        const filters = readJSON<Json>(REPORT_FILTERS_KEY, null)
        const plannerView = readJSON<Json>(PLANNER_VIEW_KEY, null)
        const financeFilters = readJSON<Json>(FINANCE_REPORT_FILTERS_KEY, null)
        if (isObject(grouping) && Object.keys(grouping).length > 0) doc.grouping = grouping
        if (isObject(filters) && Object.keys(filters).length > 0) doc.filters = filters
        if (typeof plannerView === 'string') doc.plannerView = plannerView
        if (isObject(financeFilters) && Object.keys(financeFilters).length > 0) doc.financeFilters = financeFilters
        return doc
      }
      case 'admin.pages': {
        const ordersScope = owner ? readJSON<Json>(ORDERS_SCOPE_PREFIX + owner, null) : null
        const sidebarOpen: JsonObject = {}
        for (const storageKey of keysWithPrefix(SIDEBAR_OPEN_PREFIX)) {
          if (readJSON<unknown>(storageKey, false) === true) sidebarOpen[storageKey.slice(SIDEBAR_OPEN_PREFIX.length)] = true
        }
        if (typeof ordersScope === 'string') doc.ordersScope = ordersScope
        if (Object.keys(sidebarOpen).length > 0) doc.sidebarOpen = sidebarOpen
        return doc
      }
    }
  }

  /** Puts a document (undefined: the defaults) into the browser; never counted as a user change. */
  private apply(key: SettingKey, value: JsonObject | undefined, owner: string | null = this.owner) {
    const { ui, appearance } = this.root
    const doc = value ?? {}
    this.applying = true
    try {
      runInAction(() => {
        switch (key) {
          case 'admin.appearance': {
            const { theme, density, sidebarCollapsed, ...look } = doc
            appearance.replace(look as Partial<AppearanceSettings>)
            ui.setTheme(theme === 'dark' || theme === 'system' ? theme : 'light', false)
            ui.setDensity(density === 'comfortable' ? 'comfortable' : 'compact')
            ui.setSidebarCollapsed(sidebarCollapsed === true)
            break
          }
          case 'admin.tables':
            for (const storageKey of keysWithPrefix(COLUMNS_PREFIX)) removeKey(storageKey)
            for (const [tableId, table] of Object.entries(doc)) {
              if (isObject(table) && Array.isArray(table.hidden)) writeJSON(COLUMNS_PREFIX + tableId, table.hidden.map(String))
            }
            break
          case 'admin.reports':
            if (isObject(doc.grouping)) writeJSON(REPORT_GROUPING_KEY, doc.grouping)
            else removeKey(REPORT_GROUPING_KEY)
            if (isObject(doc.filters)) writeJSON(REPORT_FILTERS_KEY, doc.filters)
            else removeKey(REPORT_FILTERS_KEY)
            if (typeof doc.plannerView === 'string') writeJSON(PLANNER_VIEW_KEY, doc.plannerView)
            else removeKey(PLANNER_VIEW_KEY)
            if (isObject(doc.financeFilters)) writeJSON(FINANCE_REPORT_FILTERS_KEY, doc.financeFilters)
            else removeKey(FINANCE_REPORT_FILTERS_KEY)
            break
          case 'admin.pages':
            for (const storageKey of keysWithPrefix(ORDERS_SCOPE_PREFIX)) removeKey(storageKey)
            if (owner && typeof doc.ordersScope === 'string') writeJSON(ORDERS_SCOPE_PREFIX + owner, doc.ordersScope)
            for (const storageKey of keysWithPrefix(SIDEBAR_OPEN_PREFIX)) removeKey(storageKey)
            if (isObject(doc.sidebarOpen)) {
              for (const [folderId, open] of Object.entries(doc.sidebarOpen)) if (open === true) writeJSON(SIDEBAR_OPEN_PREFIX + folderId, true)
            }
            break
        }
      })
    } finally {
      this.applying = false
    }
  }
}
